import React, { useState } from 'react';
import posthog from 'posthog-js';

const MaintenancePage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic email validation
    if (!email || !email.includes('@') || email.length < 5) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'maintenance_page'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || "Thanks! We'll notify you when we launch.");

        // Track successful signup with PostHog
        posthog.capture('maintenance_email_signup', {
          email_domain: email.split('@')[1],
          source: 'maintenance_page',
          timestamp: new Date().toISOString()
        });

        // Clear email field
        setEmail('');
      } else {
        throw new Error(data.error || 'Something went wrong');
      }
    } catch (error) {
      console.error('Signup failed:', error);
      setStatus('error');
      setMessage('Something went wrong. Please try again.');

      // Track error
      posthog.capture('maintenance_email_error', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4">
      <div className="max-w-2xl text-center">
        {/* Logo/Brand */}
        <div className="mb-8">
          <h1 className="text-5xl font-bold text-blue-600 mb-2">TrySnowball</h1>
          <p className="text-gray-600">Get out of debt faster</p>
        </div>

        {/* Maintenance Message */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="mb-6">
            <svg className="w-20 h-20 mx-auto text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            We're making TrySnowball even better
          </h2>

          <p className="text-gray-600 mb-6">
            Our team is working hard to bring you a faster, smarter, and more reliable
            debt-free journey. We'll be back soon with exciting new features!
          </p>

          {/* Email Capture Form */}
          {status !== 'success' ? (
            <form onSubmit={handleSubmit} className="mt-8">
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={status === 'loading'}
                  required
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? 'Subscribing...' : 'Get notified'}
                </button>
              </div>

              {status === 'error' && (
                <p className="mt-3 text-red-600 text-sm">{message}</p>
              )}

              <p className="mt-3 text-gray-500 text-sm">
                Be the first to know when we launch. No spam, promise!
              </p>
            </form>
          ) : (
            <div className="mt-8 p-4 bg-green-50 rounded-lg">
              <svg className="w-12 h-12 mx-auto text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-green-700 font-semibold">{message}</p>
            </div>
          )}
        </div>

        {/* Expected Timeline */}
        <div className="text-gray-500 text-sm">
          <p>Expected back: Very soon!</p>
          <p className="mt-2">
            Questions? Email us at{' '}
            <a href="mailto:help@trysnowball.co.uk" className="text-blue-600 hover:underline">
              help@trysnowball.co.uk
            </a>
          </p>
          <div className="mt-4 space-x-4">
            <a href="/privacy" className="text-blue-600 hover:underline">
              Privacy Policy
            </a>
            <span className="text-gray-400">•</span>
            <a href="/terms" className="text-blue-600 hover:underline">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;